import { useCallback, useMemo, useState } from 'react';
import './feedback.scss';
import type { FeedbackRequest } from '@ctw/shared/api/ai/types';
import { FeedbackThumbs } from '@ctw/shared/components/feedback-thumbs';
import { Checkbox } from '@ctw/shared/components/form/checkbox';
import { PillCheckboxGroup } from '@ctw/shared/components/form/pill-checkbox-group';
import { Heading } from '@ctw/shared/components/heading';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { isEqual } from 'lodash-es';

const FEEDBACK_POSITIVE_POLARITY_REASONS = [
  'Improved My Decisions',
  'Highlighted New Information',
  'Was Easy to Read',
  'Saved Me Time',
  'Helped Me Prepare',
  'Matched my Workflow',
] as const;

const FEEDBACK_NEGATIVE_POLARITY_REASONS = [
  'Incorrect or Outdated Information',
  'Patient Safety Concern',
  'Missing Information',
  'Incorrect Prioritization',
  'Improperly Formatted',
] as const;

export interface FeedbackProps {
  className?: ClassName;
  feedback: FeedbackRequest;
  onFeedbackUpdate: (feedback: FeedbackRequest) => void;
}

export const FeedbackComponent = ({
  feedback: initialFeedbackRequest,
  onFeedbackUpdate,
  className,
}: FeedbackProps) => {
  const [feedbackRequest, setFeedbackRequest] = useState<FeedbackRequest>(initialFeedbackRequest);

  const updateFeedback = useCallback(
    (partialFeedbackRequest: Partial<FeedbackRequest>) => {
      setFeedbackRequest((previousFeedbackRequest) => {
        const updatedFeedbackRequest = {
          ...previousFeedbackRequest,
          ...partialFeedbackRequest,
        };

        if (!isEqual(previousFeedbackRequest, updatedFeedbackRequest)) {
          onFeedbackUpdate(updatedFeedbackRequest);
        }
        return updatedFeedbackRequest;
      });
    },
    [onFeedbackUpdate],
  );

  const reasonOptions = useMemo(
    () =>
      feedbackRequest.isThumbsUp
        ? FEEDBACK_POSITIVE_POLARITY_REASONS
        : FEEDBACK_NEGATIVE_POLARITY_REASONS,
    [feedbackRequest.isThumbsUp],
  );

  return (
    <div className={twx('space-y-1', className)}>
      <div className={tw`space-y-3 pb-6`}>
        <Heading level="h4" className={tw`font-medium text-2xl text-content-subtle`}>
          What Did You Think of this Summary?
        </Heading>
        <FeedbackThumbs
          initialValue={feedbackRequest.isThumbsUp}
          onChange={(isThumbsUp) =>
            updateFeedback({ isThumbsUp: Boolean(isThumbsUp), reasons: [] })
          }
        />
      </div>
      <PillCheckboxGroup
        className={tw`text-sm`}
        options={reasonOptions}
        defaultSelected={feedbackRequest.reasons}
        onChange={(selected) => updateFeedback({ reasons: selected })}
        legend="Why did you choose this rating?"
      />
      <div className={tw`pt-4 pb-2`}>
        <label>
          <div className={tw`font-medium text-sm`}>
            Is there any other feedback you would like to share about Patient Summaries?
          </div>
          <textarea
            className={tw`listbox-textarea mt-2 w-full resize-none whitespace-pre-wrap`}
            value={feedbackRequest.content}
            name="accuracy_textarea"
            onChange={(event) => updateFeedback({ content: event.target.value })}
          />
        </label>
      </div>
      <div>
        <Checkbox
          className={tw`font-medium text-sm`}
          checked={feedbackRequest.contactPermission ?? true}
          onChange={(contactPermission) => {
            updateFeedback({ contactPermission });
          }}
          name="contactPermission"
          label="It’s okay to reach out to me about my responses"
        />
      </div>
    </div>
  );
};
